import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Residencial", "Restaurante / Café", "Hotel", "Oficina / Comercio", "Otro"];

const inputCls =
  "w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 outline-none transition-colors focus:border-amber-500 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100";

export const QuoteSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid = form.name && (form.email.includes("@") || form.phone) && form.message;

  const submit = async () => {
    if (!valid) {
      toast.error("Completa tu nombre, un contacto y cuéntanos tu proyecto");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("¡Cotización recibida! Te contactaremos muy pronto.");
      setForm({ name: "", email: "", phone: "", project_type: PROJECT_TYPES[0], message: "" });
    } catch {
      toast.error("No pudimos enviar tu solicitud, intenta de nuevo");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="cotiza" data-testid="quote-section" className="bg-[#F4F0EA] px-6 py-28 dark:bg-[#141210]">
      <div className="mx-auto grid max-w-7xl items-start gap-14 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-amber-700/80 dark:text-amber-400/90">
            Proyectos a medida
          </p>
          <h2 className="font-serif text-3xl font-light leading-tight tracking-tight text-stone-900 dark:text-stone-50 sm:text-4xl lg:text-5xl">
            Cotiza tu proyecto de iluminación
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-stone-600 dark:text-stone-300">
            ¿Un restaurante, un hotel, una casa completa? Trabajamos con todo tipo de luminarias:
            colgantes, apliques, lámparas de pie y de mesa, y focos de toda clase.
            Cuéntanos tu proyecto y te enviamos una cotización personalizada.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-stone-600 dark:text-stone-300">
            {["Asesoría lumínica sin costo", "Todo tipo de focos y pantallas", "Precios especiales por volumen"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-600 dark:bg-amber-400" />
                {t}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl border border-stone-200/80 bg-white/70 p-8 backdrop-blur-sm dark:border-stone-800 dark:bg-stone-900/60"
        >
          <div className="space-y-3">
            <input data-testid="quote-name-input" placeholder="Nombre completo" value={form.name} onChange={set("name")} className={inputCls} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input data-testid="quote-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="quote-phone-input" placeholder="Teléfono / WhatsApp" value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <select data-testid="quote-type-select" value={form.project_type} onChange={set("project_type")} className={inputCls}>
              {PROJECT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <textarea
              data-testid="quote-message-input"
              placeholder="Cuéntanos tu proyecto: espacio, cantidad de luminarias, estilo que buscas..."
              value={form.message}
              onChange={set("message")}
              rows={4}
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="quote-submit-button"
              onClick={submit}
              disabled={sending}
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-stone-900 py-4 text-sm font-medium text-[#FDFBF7] transition-all duration-300 enabled:hover:bg-amber-700 disabled:opacity-60 dark:bg-stone-50 dark:text-stone-900 dark:enabled:hover:bg-amber-400"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : (
                <>Solicitar cotización <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" /></>
              )}
            </button>
            <p className="text-center text-[11px] text-stone-400">Respondemos en menos de 24 horas.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
